import React, { useEffect, useRef, useState } from "react";
import { BellIcon, ArrowPathIcon } from "@heroicons/react/24/outline";
import logo from "../assets/finai_nobg.png";

/**
 * Writes a 0→1 "--reveal" CSS variable on the element each scroll frame so
 * the mockup's CSS rotateX can flatten as it enters the viewport.
 */
const useScrollReveal = () => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let raf = 0;
    const update = () => {
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight;
      const start = vh * 0.9;
      const end = vh * 0.2;
      const raw = (start - rect.top) / (start - end);
      const clamped = Math.max(0, Math.min(1, raw));
      el.style.setProperty("--reveal", clamped.toString());
    };
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      cancelAnimationFrame(raf);
    };
  }, []);
  return ref;
};

const TABS = [
  { id: "overview", label: "Overview", slug: "overview" },
  { id: "performance", label: "Performance", slug: "performance" },
  { id: "holdings", label: "Holdings", slug: "holdings" },
  { id: "insights", label: "Insights", slug: "insights" },
  { id: "accounts", label: "Linked Accounts", slug: "accounts" },
  { id: "blogs", label: "Blogs", slug: "blogs" },
  { id: "community", label: "Community", slug: "community" },
];

const TICKER_ITEMS = [
  { sym: "AAPL", price: "$186.45", chg: "+0.62%", up: true },
  { sym: "MSFT", price: "$412.30", chg: "+1.84%", up: true },
  { sym: "NVDA", price: "$948.20", chg: "+2.41%", up: true },
  { sym: "GOOGL", price: "$171.50", chg: "-0.42%", up: false },
  { sym: "TSLA", price: "$223.80", chg: "-1.20%", up: false },
  { sym: "AMZN", price: "$182.40", chg: "+0.88%", up: true },
  { sym: "META", price: "$507.10", chg: "+1.12%", up: true },
  { sym: "BTC", price: "$67,420", chg: "+3.41%", up: true },
];

const Ticker = () => (
  <div className="overflow-hidden border-b border-white/10 bg-black/60 relative">
    <div className="flex gap-8 px-5 py-2.5 text-[12px] font-bold whitespace-nowrap animate-ticker w-max">
      {[...TICKER_ITEMS, ...TICKER_ITEMS].map((t, i) => (
        <span key={i} className="inline-flex items-center gap-2 flex-shrink-0">
          <span className="text-white">{t.sym}</span>
          <span className="text-gray-300">{t.price}</span>
          <span className={t.up ? "text-emerald-400" : "text-rose-400"}>{t.chg}</span>
        </span>
      ))}
    </div>
  </div>
);

/* ---------- Overview ---------- */

const OverviewView = () => (
  <>
    <Ticker />
    <div className="p-6 md:p-10">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
        <div className="flex items-center gap-3 flex-wrap">
          <h2 className="text-3xl md:text-4xl font-extrabold text-white">
            Portfolio Analysis
          </h2>
          <span className="px-2 py-1 rounded-md text-[10px] font-bold tracking-widest bg-indigo-500/15 text-indigo-300 border border-indigo-500/30">
            SAMPLE · DEMO DATA
          </span>
        </div>
        {/* Same rotating gradient frame + fluid interior as the hero CTA
            GlowButton, so the mockup visually anchors to the page's button
            language. Inlined (not the <GlowButton/> component) because that
            component renders an <a>/<button> with link semantics; this is
            decoration inside a screenshot. */}
        <span className="glow-border inline-block rounded-full">
          <span className="fluid-bg relative block rounded-full bg-black text-white font-bold tracking-wide px-4 py-1.5 text-xs">
            <span className="relative z-10 inline-flex items-center gap-1.5">
              <ArrowPathIcon className="w-3.5 h-3.5" /> Refresh Data
            </span>
          </span>
        </span>
      </div>

      <p className="text-sm md:text-base text-gray-400 mb-3 max-w-3xl">
        Link your brokerage accounts, visualize performance vs. S&amp;P 500, and
        let FinAI highlight risks, concentration, and opportunities automatically.
      </p>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-400 mb-6">
        <span className="inline-flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-rose-500" />
          Market Closed · Weekend
        </span>
        <span>08:50:49 PM</span>
        <span>Last synced: 4/9/2026, 7:07:11 PM</span>
      </div>

      <div className="rounded-xl border border-amber-500/30 bg-amber-500/[0.05] p-5 md:p-6 mb-6">
        <div className="flex items-center gap-2 mb-3">
          <span className="px-2 py-1 rounded-md text-[10px] font-bold bg-amber-500/20 text-amber-300 border border-amber-500/30">
            WATCH · COMPASS
          </span>
          <span className="text-xs text-gray-500">May 9, 12:12 PM</span>
        </div>
        <h3 className="text-lg md:text-xl font-bold text-white mb-3">
          This sample portfolio is up 12.4% YTD, totaling $84,200.
        </h3>
        <ul className="space-y-1.5 text-sm md:text-base text-gray-300">
          {[
            "Equity weight is 72% — 12pts above the 60% target Compass uses for moderate risk profiles.",
            "NVDA is the largest position at $15,200 (18% of portfolio).",
            "Total gain since inception is $9,280 (+12.4%) — comfortably above benchmark.",
            "Consider diversifying into bonds or commodities to mitigate equity concentration.",
            "TSLA is down 8.2% this month — Vigil suggests holding through earnings.",
          ].map((b) => (
            <li key={b} className="flex gap-2">
              <span className="text-amber-400">•</span>
              <span>{b}</span>
            </li>
          ))}
        </ul>
        <button
          type="button"
          className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-amber-300 hover:text-amber-200 transition-colors"
        >
          💬 Ask the Council why →
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        {[
          { l: "Total Portfolio Value", v: "$84,200", tone: "white" },
          { l: "Today's Change", v: "+$1,040", sub: "+1.25%", tone: "emerald" },
          { l: "Total Return (since inception)", v: "+$9,280", sub: "+12.4%", tone: "emerald" },
          { l: "Risk Score", v: "Moderate (6/10)", tone: "amber" },
        ].map((s) => (
          <div
            key={s.l}
            className="rounded-xl bg-white/[0.03] border border-white/10 p-4 md:p-5"
          >
            <p className="text-[10px] uppercase tracking-wider text-gray-400 mb-2">
              {s.l}
            </p>
            <p
              className={`text-2xl md:text-3xl font-extrabold ${
                s.tone === "emerald"
                  ? "text-emerald-400"
                  : s.tone === "rose"
                  ? "text-rose-400"
                  : s.tone === "amber"
                  ? "text-amber-400"
                  : "text-white"
              }`}
            >
              {s.v}
            </p>
            {s.sub && (
              <p className="text-xs text-emerald-400 font-semibold mt-1">{s.sub}</p>
            )}
          </div>
        ))}
      </div>
    </div>
  </>
);

/* ---------- Performance ---------- */

const PerformanceView = () => (
  <div className="p-6 md:p-10">
    <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-1">
      Performance
    </h2>
    <p className="text-sm md:text-base text-gray-400 mb-6">
      Sample portfolio · time-weighted return vs. S&amp;P 500.
    </p>

    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-6">
      {[
        { v: "+2.1%", l: "MTD" },
        { v: "+12.4%", l: "YTD" },
        { v: "+24.5%", l: "Since inception" },
        { v: "+3.8%", l: "vs. S&P 500" },
      ].map((s) => (
        <div
          key={s.l}
          className="rounded-xl bg-white/[0.03] border border-white/10 p-4 md:p-5"
        >
          <p className="text-2xl md:text-3xl font-extrabold text-emerald-400">
            {s.v}
          </p>
          <p className="text-[10px] uppercase tracking-wider text-gray-400 mt-1">
            {s.l}
          </p>
        </div>
      ))}
    </div>

    <div className="rounded-xl bg-white/[0.03] border border-white/10 p-5">
      <p className="text-xs uppercase tracking-wider text-gray-400 mb-3">
        Equity curve · 12 months
      </p>
      <svg viewBox="0 0 400 100" className="w-full h-28" preserveAspectRatio="none">
        <defs>
          <linearGradient id="perfGrad2" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#10b981" stopOpacity="0.45" />
            <stop offset="100%" stopColor="#10b981" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path
          d="M0,82 L30,75 L60,80 L90,64 L120,68 L150,52 L180,58 L210,44 L240,48 L270,30 L300,36 L330,22 L360,18 L400,8 L400,100 L0,100 Z"
          fill="url(#perfGrad2)"
        />
        <path
          d="M0,82 L30,75 L60,80 L90,64 L120,68 L150,52 L180,58 L210,44 L240,48 L270,30 L300,36 L330,22 L360,18 L400,8"
          fill="none"
          stroke="#10b981"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>

    {/* Best / worst contributors */}
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4 mt-4">
      <div className="rounded-xl bg-white/[0.03] border border-white/10 p-5">
        <p className="text-xs uppercase tracking-wider text-gray-400 mb-3">
          Best contributors YTD
        </p>
        {[
          { tkr: "NVDA", val: "+$5,420", pct: "+84%" },
          { tkr: "BTC", val: "+$1,890", pct: "+34%" },
          { tkr: "MSFT", val: "+$1,210", pct: "+22%" },
        ].map((row) => (
          <div
            key={row.tkr}
            className="flex items-center justify-between py-2 border-b border-white/5 last:border-0"
          >
            <span className="text-sm font-bold text-white">{row.tkr}</span>
            <div className="flex items-center gap-4">
              <span className="text-sm text-emerald-400 font-semibold tabular-nums">
                {row.val}
              </span>
              <span className="text-xs text-emerald-400 tabular-nums w-12 text-right">
                {row.pct}
              </span>
            </div>
          </div>
        ))}
      </div>
      <div className="rounded-xl bg-white/[0.03] border border-white/10 p-5">
        <p className="text-xs uppercase tracking-wider text-gray-400 mb-3">
          Detractors YTD
        </p>
        {[
          { tkr: "TSLA", val: "-$580", pct: "-8%" },
          { tkr: "META", val: "-$210", pct: "-2%" },
          { tkr: "PYPL", val: "-$190", pct: "-12%" },
        ].map((row) => (
          <div
            key={row.tkr}
            className="flex items-center justify-between py-2 border-b border-white/5 last:border-0"
          >
            <span className="text-sm font-bold text-white">{row.tkr}</span>
            <div className="flex items-center gap-4">
              <span className="text-sm text-rose-400 font-semibold tabular-nums">
                {row.val}
              </span>
              <span className="text-xs text-rose-400 tabular-nums w-12 text-right">
                {row.pct}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

/* ---------- Holdings ---------- */

const HOLDINGS = [
  { tkr: "NVDA", name: "NVIDIA Corp", weight: "18.05%", value: "$15,200", chg: "+2.41%", up: true },
  { tkr: "AAPL", name: "Apple Inc", weight: "14.20%", value: "$11,956", chg: "+0.62%", up: true },
  { tkr: "MSFT", name: "Microsoft Corp", weight: "12.80%", value: "$10,778", chg: "+1.84%", up: true },
  { tkr: "BTC", name: "Bitcoin", weight: "9.80%", value: "$8,252", chg: "+3.41%", up: true },
  { tkr: "AMZN", name: "Amazon.com", weight: "8.40%", value: "$7,073", chg: "+0.88%", up: true },
];

const HoldingsView = () => (
  <div className="p-6 md:p-10">
    <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-1">
      Holdings
    </h2>
    <p className="text-sm md:text-base text-gray-400 mb-6">
      Sample portfolio · 5 positions · $84,200 total · synced 9 minutes ago.
    </p>
    <div className="rounded-xl border border-white/10 overflow-hidden">
      <div className="grid grid-cols-[1fr_80px_100px_90px] md:grid-cols-[1fr_120px_140px_110px] gap-3 px-5 py-3 text-[10px] uppercase tracking-wider text-gray-500 border-b border-white/10 bg-black/30">
        <span>Position</span>
        <span className="text-right">Weight</span>
        <span className="text-right hidden sm:block">Value</span>
        <span className="text-right">Day</span>
      </div>
      {HOLDINGS.map((h) => (
        <div
          key={h.tkr}
          className="grid grid-cols-[1fr_80px_100px_90px] md:grid-cols-[1fr_120px_140px_110px] gap-3 px-5 py-3 items-center border-b border-white/5 last:border-b-0 hover:bg-white/[0.02] transition-colors"
        >
          <div className="flex items-center gap-3 min-w-0">
            <span className="w-9 h-9 rounded-md bg-gradient-to-br from-emerald-500/30 to-sky-500/30 text-white text-[10px] font-extrabold flex items-center justify-center flex-shrink-0">
              {h.tkr.slice(0, 2)}
            </span>
            <div className="min-w-0">
              <p className="text-sm font-bold text-white truncate">{h.tkr}</p>
              <p className="text-[11px] text-gray-500 truncate">{h.name}</p>
            </div>
          </div>
          <span className="text-sm font-semibold text-gray-200 text-right tabular-nums">
            {h.weight}
          </span>
          <span className="text-sm font-semibold text-gray-200 text-right tabular-nums hidden sm:block">
            {h.value}
          </span>
          <span
            className={`text-sm font-bold text-right tabular-nums ${
              h.up ? "text-emerald-400" : "text-rose-400"
            }`}
          >
            {h.chg}
          </span>
        </div>
      ))}
    </div>

    {/* Allocation by asset class — kept compact so the dashboard frame
        on the Holdings tab matches the other tabs' height. */}
    <div className="mt-3 rounded-xl bg-white/[0.03] border border-white/10 p-3">
      <p className="text-xs uppercase tracking-wider text-gray-400 mb-2">
        Allocation by class
      </p>
      <div className="space-y-1.5">
        {[
          { label: "US equity", pct: 62, color: "#10b981" },
          { label: "Crypto", pct: 18, color: "#3b82f6" },
          { label: "Real estate", pct: 9, color: "#a855f7" },
          { label: "Bonds & cash", pct: 11, color: "#6366f1" },
        ].map((row) => (
          <div key={row.label}>
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="text-gray-300">{row.label}</span>
              <span className="font-bold text-white tabular-nums">
                {row.pct}%
              </span>
            </div>
            <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
              <div
                className="h-full rounded-full"
                style={{
                  width: `${row.pct}%`,
                  backgroundColor: row.color,
                }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

/* ---------- Insights ---------- */

const INSIGHTS = [
  { agent: "Compass", color: "#10b981", line: "Equity weight is 72% — 12pts above the 60% target. Trim or accept drift." },
  { agent: "Lens", color: "#3b82f6", line: "NVDA P/E sits at 43 — momentum stretched, but earnings growth supports it. Hold." },
  { agent: "Vigil", color: "#f43f5e", line: "Single-name concentration (NVDA 18%) is the top risk in a tech-sector correction." },
  { agent: "Tally", color: "#f59e0b", line: "$4,200 in LTCG headroom this year — harvest long-term gains tax-efficiently." },
];

const InsightsView = () => (
  <div className="p-6 md:p-10">
    <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-1">
      Insights
    </h2>
    <p className="text-sm md:text-base text-gray-400 mb-6">
      The Council · 4 fresh insights today.
    </p>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
      {INSIGHTS.map((i) => (
        <div
          key={i.agent}
          className="rounded-xl bg-white/[0.03] border border-white/10 p-4 md:p-5"
        >
          <div className="flex items-center gap-2.5 mb-2">
            <span
              className="w-9 h-9 rounded-lg flex items-center justify-center text-sm font-extrabold"
              style={{ backgroundColor: `${i.color}22`, color: i.color }}
            >
              {i.agent[0]}
            </span>
            <p className="text-sm md:text-base font-bold text-white">{i.agent}</p>
          </div>
          <p className="text-sm md:text-base text-gray-300 leading-relaxed">
            {i.line}
          </p>
        </div>
      ))}
    </div>

    {/* Action queue — concrete next steps the Council recommends */}
    <div className="mt-5 rounded-xl border border-emerald-500/30 bg-emerald-500/[0.05] p-5">
      <div className="flex items-center gap-2 mb-3">
        <span className="px-2 py-1 rounded-md text-[10px] font-bold tracking-widest bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
          ACTION QUEUE
        </span>
        <span className="text-xs text-gray-500">3 items today</span>
      </div>
      <ul className="space-y-2.5 text-sm text-gray-300">
        {[
          {
            agent: "Compass",
            text: "Rebalance: shift 16pp from tech → international + bonds",
          },
          {
            agent: "Tally",
            text: "Harvest ZOMATO loss (−₹38K) before Mar 31",
          },
          {
            agent: "Vigil",
            text: "Set 5% trim alert on NVDA above $1,000",
          },
        ].map((row, idx) => (
          <li
            key={idx}
            className="flex items-start gap-2.5 border-b border-white/5 last:border-0 pb-2.5 last:pb-0"
          >
            <span className="text-emerald-400 mt-0.5 font-bold">→</span>
            <span>
              <span className="font-bold text-white">{row.agent}:</span>{" "}
              {row.text}
            </span>
          </li>
        ))}
      </ul>
    </div>
  </div>
);

/* ---------- Linked Accounts ---------- */

const ACCOUNTS = [
  { name: "Interactive Brokers", type: "Brokerage · global equity", value: "$54,200", live: true },
  { name: "Robinhood", type: "US equity", value: "$18,800", live: true },
  { name: "Coinbase", type: "Crypto exchange", value: "$8,200", live: true },
  { name: "401(k)", type: "Manual asset", value: "$2,500", live: false },
  { name: "Gold ETF", type: "Manual asset", value: "$500", live: false },
];

const AccountsView = () => (
  <div className="p-6 md:p-10">
    <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-1">
      Linked Accounts
    </h2>
    <p className="text-sm md:text-base text-gray-400 mb-6">
      Sample · 3 live · 2 manual · $84,200 across all sources.
    </p>
    <div className="rounded-xl border border-white/10 overflow-hidden">
      {ACCOUNTS.map((a) => (
        <div
          key={a.name}
          className="flex items-center gap-4 px-5 py-3 border-b border-white/5 last:border-b-0"
        >
          <span className="w-9 h-9 rounded-lg bg-gradient-to-br from-sky-500/30 to-emerald-500/30 flex items-center justify-center text-white text-sm font-extrabold flex-shrink-0">
            {a.name[0]}
          </span>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <p className="text-sm font-bold text-white truncate">{a.name}</p>
              {a.live ? (
                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/40">
                  LIVE
                </span>
              ) : (
                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-white/5 text-gray-400 border border-white/15">
                  MANUAL
                </span>
              )}
            </div>
            <p className="text-[11px] text-gray-500 truncate">{a.type}</p>
          </div>
          <span className="text-sm font-bold text-white tabular-nums">{a.value}</span>
        </div>
      ))}
    </div>

    {/* Sync activity log — shows live-feed feel of brokerage sync */}
    <div className="mt-4 rounded-xl bg-white/[0.03] border border-white/10 p-4">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs uppercase tracking-wider text-gray-400">
          Recent sync activity
        </p>
        <span className="text-[10px] text-gray-500">last 24 hours</span>
      </div>
      <div className="space-y-2.5">
        {[
          {
            source: "Interactive Brokers",
            action: "Position sync · 12 holdings updated",
            time: "2m ago",
          },
          {
            source: "Coinbase",
            action: "Balance sync · 3 wallets refreshed",
            time: "12m ago",
          },
        ].map((row, idx) => (
          <div
            key={idx}
            className="flex items-start gap-3 pb-2.5 border-b border-white/5 last:border-0 last:pb-0"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-white">{row.source}</p>
              <p className="text-xs text-gray-400">{row.action}</p>
            </div>
            <span className="text-[11px] text-gray-500 flex-shrink-0">
              {row.time}
            </span>
          </div>
        ))}
      </div>
    </div>
  </div>
);

/* ---------- Blogs ---------- */

const BLOG_POSTS = [
  { tag: "MACRO", title: "Navigating the 2026 rate-cut cycle", excerpt: "Compass walks through the three scenarios most relevant for Indian retail portfolios.", date: "May 8 · 5 min" },
  { tag: "RISK", title: "Why concentration risk kills portfolios", excerpt: "Vigil reviews the three concentration metrics that actually predict drawdowns.", date: "May 6 · 4 min" },
  { tag: "TAX", title: "Tax-loss harvesting, simplified", excerpt: "Tally on using your annual capital-gains exemption every year without tripping wash-sale rules.", date: "May 3 · 7 min" },
];

const BlogsView = () => (
  <div className="p-6 md:p-10">
    <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-1">Blogs</h2>
    <p className="text-sm md:text-base text-gray-400 mb-6">
      Council writing · weekly long-form from the specialists.
    </p>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
      {BLOG_POSTS.map((b) => (
        <div
          key={b.title}
          className="rounded-xl bg-white/[0.03] border border-white/10 p-4 md:p-5 flex flex-col"
        >
          <span className="text-[10px] font-bold tracking-widest text-indigo-300 mb-2">
            {b.tag}
          </span>
          <h3 className="text-base md:text-lg font-bold text-white mb-2 leading-snug">
            {b.title}
          </h3>
          <p className="text-sm text-gray-400 leading-relaxed flex-1">
            {b.excerpt}
          </p>
          <p className="text-[11px] text-gray-500 mt-3">{b.date}</p>
        </div>
      ))}
    </div>
  </div>
);

/* ---------- Community ---------- */

const COMMUNITY = [
  { handle: "@anita_invests", name: "Anita S.", ytd: "+34.5%", post: "Trimmed NVDA following Vigil concentration alert. Net -2% but slept better." },
  { handle: "@raghav_w", name: "Raghav W.", ytd: "+19.2%", post: "Used Tally's harvesting suggestion last week — saved ~$420 in projected tax." },
  { handle: "@deepa_mf", name: "Deepa K.", ytd: "+11.8%", post: "Public portfolio is up. Asked the Council before every buy this quarter." },
];

const CommunityView = () => (
  <div className="p-6 md:p-10">
    <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-1">Community</h2>
    <p className="text-sm md:text-base text-gray-400 mb-6">
      How other investors are using the Council.
    </p>
    <div className="space-y-3 md:space-y-4">
      {COMMUNITY.map((c) => (
        <div
          key={c.handle}
          className="rounded-xl bg-white/[0.03] border border-white/10 p-4 md:p-5 flex gap-4"
        >
          <span className="w-11 h-11 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-extrabold flex-shrink-0">
            {c.name[0]}
          </span>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <p className="text-sm font-bold text-white">{c.name}</p>
              <span className="text-xs text-gray-500">{c.handle}</span>
              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/40">
                YTD {c.ytd}
              </span>
            </div>
            <p className="text-sm text-gray-300 leading-relaxed">{c.post}</p>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const VIEWS = {
  overview: <OverviewView />,
  performance: <PerformanceView />,
  holdings: <HoldingsView />,
  insights: <InsightsView />,
  accounts: <AccountsView />,
  blogs: <BlogsView />,
  community: <CommunityView />,
};

/* ---------- Component ---------- */

const HeroDashboardMockup = () => {
  const ref = useScrollReveal();
  const [activeId, setActiveId] = useState("overview");
  const activeSlug = TABS.find((t) => t.id === activeId)?.slug ?? "overview";

  return (
    <div className="hero-mockup-stage mt-6 mb-4">
      <div ref={ref} className="hero-mockup mx-auto max-w-[1500px]">
        {/* Window chrome */}
        <div className="hero-mockup__chrome">
          <span className="hero-mockup__dot" style={{ background: "#ff5f57" }} />
          <span className="hero-mockup__dot" style={{ background: "#febc2e" }} />
          <span className="hero-mockup__dot" style={{ background: "#28c840" }} />
          <span className="hero-mockup__url">finai.ai/portfolio/{activeSlug}</span>
        </div>

        {/* App navbar inside the window */}
        <div className="border-b border-white/10 bg-black/30">
          <div className="flex items-center gap-4 md:gap-6 px-5 md:px-6 py-3 overflow-x-auto">
            <div className="flex items-center gap-2 flex-shrink-0">
              <img src={logo} alt="FinAI" className="w-8 h-8" />
              <span className="font-extrabold text-white">FinAI</span>
            </div>

            <nav className="flex items-center gap-1 md:gap-2 flex-shrink-0">
              {TABS.map((tab) => {
                const active = tab.id === activeId;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveId(tab.id)}
                    className={
                      active
                        ? "px-3 py-1.5 text-sm font-semibold text-indigo-400 whitespace-nowrap"
                        : "px-3 py-1.5 text-sm font-medium text-gray-400 hover:text-white whitespace-nowrap transition-colors"
                    }
                  >
                    {tab.label}
                  </button>
                );
              })}
            </nav>

            <div className="hidden lg:flex items-center gap-3 ml-auto flex-shrink-0">
              <span className="relative inline-flex">
                <BellIcon className="w-5 h-5 text-gray-400" />
                <span className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-rose-500 text-[9px] font-bold text-white flex items-center justify-center">
                  2
                </span>
              </span>
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-pink-500" />
            </div>
          </div>
        </div>

        {/* Content — responsive min-height so the dashboard looks
            proportionate on every screen. Bigger floor on desktop, more
            compact on laptops/tablets. Set tight enough that even the
            sparsest tabs (Community, Blogs) don't feel like empty
            wells, but loose enough that Linked Accounts fits. */}
        <div className="text-left min-h-[560px] md:min-h-[640px] lg:min-h-[720px]">
          {VIEWS[activeId]}
        </div>
      </div>
    </div>
  );
};

export default HeroDashboardMockup;
